import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { IndexedUserData, UserAnalyticsSortField, SortDirection, UserAnalyticsFilter } from '../types/indexedUser';

export interface UseIndexedUserDataReturn {
  users: IndexedUserData[];
  loading: boolean;
  error: Error | null;
  refreshData: () => Promise<void>;
  sortField: UserAnalyticsSortField;
  sortDirection: SortDirection;
  setSortField: (field: UserAnalyticsSortField) => void;
  setSortDirection: (direction: SortDirection) => void;
  filter: UserAnalyticsFilter;
  setFilter: (filter: UserAnalyticsFilter) => void;
  filteredUsers: IndexedUserData[];
  stats: UserAnalyticsStats;
}

export interface UserAnalyticsStats {
  totalUsers: number;
  platinumUsers: number;
  premiumUsers: number;
  coachUsers: number;
  plusUsers: number;
  defaultUsers: number;
  usersWithWearables: number;
  wearableBreakdown: { resource: string; count: number }[];
  // Totals across all users
  totalDayPosts: number;
  totalPosts: number;
  totalAiChats: number;
  totalTrainingLogs: number;
  totalActivityLogs: number;
  totalSessions: number;
  avgDayPostsPerUser: number;
  lastIndexedAt: Date | null;
}

/**
 * Hook to fetch and manage indexed user data for analytics
 * Fetches from the pre-indexed indexedUserData collection for fast loading
 */
export function useIndexedUserData(): UseIndexedUserDataReturn {
  const [users, setUsers] = useState<IndexedUserData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [sortField, setSortField] = useState<UserAnalyticsSortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [filter, setFilter] = useState<UserAnalyticsFilter>({ minDayPosts: 3 });

  // Convert Firestore Timestamp to Date
  const convertTimestamp = (timestamp: any): Date | null => {
    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    }
    try {
      return new Date(timestamp);
    } catch {
      return null;
    }
  };

  const fetchUsers = useCallback(async () => {
    console.log('[INDEXED_USER_DATA] Fetching indexed user data...');
    setLoading(true);
    setError(null);

    try {
      const indexedUsersQuery = query(
        collection(db, 'indexedUserData'),
        orderBy('name', 'asc')
      );
      
      const snapshot = await getDocs(indexedUsersQuery);
      
      const indexedUsers: IndexedUserData[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || '',
          surname: data.surname || '',
          email: data.email || '',
          username: data.username || '',
          imageUrl: data.imageUrl || undefined,
          role: data.role || '',
          sport: data.sport || '',
          deActivated: data.deActivated || false,
          onboardingComplete: data.onboardingComplete || false,
          subscriptionType: data.subscriptionType || 'default',
          subscriptionVia: data.subscriptionVia || 'default',
          subscriptionLastUpdated: convertTimestamp(data.subscriptionLastUpdated),
          stripeCustomerId: data.stripeCustomerId || null,
          stripeSubscriptionId: data.stripeSubscriptionId || null,
          stripeSubscriptionStatus: data.stripeSubscriptionStatus || null,
          stripePriceId: data.stripePriceId || null,
          dayPostsCount: data.dayPostsCount || 0,
          postsCount: data.postsCount || 0,
          aiChatsCount: data.aiChatsCount || 0,
          wearableDevicesCount: data.wearableDevicesCount || 0,
          wearableDevices: data.wearableDevices || [],
          trainingLogsCount: data.trainingLogsCount || 0,
          activityLogsCount: data.activityLogsCount || 0,
          sessionsCount: data.sessionsCount || 0,
          orgIds: data.orgIds || [],
          organisationId: data.organisationId || undefined,
          lastIndexedAt: convertTimestamp(data.lastIndexedAt) || new Date(),
          indexVersion: data.indexVersion || 1,
        } as IndexedUserData;
      });

      console.log(`[INDEXED_USER_DATA] Successfully fetched ${indexedUsers.length} indexed users`);
      setUsers(indexedUsers);
      setError(null);
    } catch (err) {
      console.error('[INDEXED_USER_DATA] Error fetching indexed users:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch indexed users'));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    await fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Apply filtering
  const filteredUsers = users.filter((user) => {
    // Search term filter
    if (filter.searchTerm) {
      const searchLower = filter.searchTerm.toLowerCase();
      const matchesSearch =
        user.name.toLowerCase().includes(searchLower) ||
        user.surname.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.id.toLowerCase().includes(searchLower) ||
        user.username.toLowerCase().includes(searchLower) ||
        user.subscriptionType.toLowerCase().includes(searchLower) ||
        user.subscriptionVia.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Subscription type filter
    if (filter.subscriptionType && user.subscriptionType !== filter.subscriptionType) {
      return false;
    }

    // Subscription via filter
    if (filter.subscriptionVia && user.subscriptionVia !== filter.subscriptionVia) {
      return false;
    }

    // Has wearable filter
    if (filter.hasWearable !== undefined) {
      const hasWearable = user.wearableDevicesCount > 0;
      if (filter.hasWearable !== hasWearable) return false;
    }

    // Min day posts filter
    if (filter.minDayPosts !== undefined && user.dayPostsCount < filter.minDayPosts) {
      return false;
    }

    return true;
  });

  // Apply sorting
  const sortedFilteredUsers = [...filteredUsers].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    switch (sortField) {
      case 'name':
        aVal = `${a.name} ${a.surname}`.toLowerCase();
        bVal = `${b.name} ${b.surname}`.toLowerCase();
        break;
      case 'email':
        aVal = a.email.toLowerCase();
        bVal = b.email.toLowerCase();
        break;
      case 'subscriptionType':
        aVal = a.subscriptionType;
        bVal = b.subscriptionType;
        break;
      case 'dayPostsCount':
        aVal = a.dayPostsCount;
        bVal = b.dayPostsCount;
        break;
      case 'postsCount':
        aVal = a.postsCount;
        bVal = b.postsCount;
        break;
      case 'aiChatsCount':
        aVal = a.aiChatsCount;
        bVal = b.aiChatsCount;
        break;
      case 'wearableDevicesCount':
        aVal = a.wearableDevicesCount;
        bVal = b.wearableDevicesCount;
        break;
      case 'trainingLogsCount':
        aVal = a.trainingLogsCount;
        bVal = b.trainingLogsCount;
        break;
      case 'activityLogsCount':
        aVal = a.activityLogsCount;
        bVal = b.activityLogsCount;
        break;
      case 'sessionsCount':
        aVal = a.sessionsCount;
        bVal = b.sessionsCount;
        break;
      case 'lastIndexedAt':
        aVal = a.lastIndexedAt?.getTime() || 0;
        bVal = b.lastIndexedAt?.getTime() || 0;
        break;
      default:
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Calculate stats
  const totalDayPosts = users.reduce((sum, u) => sum + u.dayPostsCount, 0);

  // Count users per wearable resource type (a user with two devices of the
  // same brand only counts once for that brand)
  const wearableBreakdownMap: Record<string, number> = {};
  users.forEach((u) => {
    const resources = new Set(
      u.wearableDevices
        .map((d) => d.resource?.toUpperCase())
        .filter((resource): resource is string => !!resource)
    );
    resources.forEach((resource) => {
      wearableBreakdownMap[resource] = (wearableBreakdownMap[resource] || 0) + 1;
    });
  });
  const wearableBreakdown = Object.entries(wearableBreakdownMap)
    .map(([resource, count]) => ({ resource, count }))
    .sort((a, b) => b.count - a.count);

  const stats: UserAnalyticsStats = {
    totalUsers: users.length,
    platinumUsers: users.filter((u) => u.subscriptionType === 'platinum').length,
    premiumUsers: users.filter((u) => u.subscriptionType === 'premium').length,
    coachUsers: users.filter((u) => u.subscriptionType === 'coach').length,
    plusUsers: users.filter((u) => u.subscriptionType === 'plus').length,
    defaultUsers: users.filter(
      (u) => u.subscriptionType === 'default' || !u.subscriptionType
    ).length,
    usersWithWearables: users.filter((u) => u.wearableDevicesCount > 0).length,
    wearableBreakdown,
    totalDayPosts,
    totalPosts: users.reduce((sum, u) => sum + u.postsCount, 0),
    totalAiChats: users.reduce((sum, u) => sum + u.aiChatsCount, 0),
    totalTrainingLogs: users.reduce((sum, u) => sum + u.trainingLogsCount, 0),
    totalActivityLogs: users.reduce((sum, u) => sum + u.activityLogsCount, 0),
    totalSessions: users.reduce((sum, u) => sum + u.sessionsCount, 0),
    avgDayPostsPerUser:
      users.length > 0
        ? Math.round((totalDayPosts / users.length) * 10) / 10
        : 0,
    lastIndexedAt: users.length > 0 ? users[0].lastIndexedAt : null,
  };

  return {
    users,
    loading,
    error,
    refreshData,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    filter,
    setFilter,
    filteredUsers: sortedFilteredUsers,
    stats,
  };
}

